import {
  animationFrameScheduler,
  asapScheduler,
  asyncScheduler,
  defer,
  EMPTY,
  from,
  MonoTypeOperatorFunction,
  Observable,
  ObservableInput,
  observeOn,
  queueScheduler,
  scheduled,
  SchedulerLike,
  subscribeOn,
  switchMap,
  timer,
} from 'rxjs';
import { take } from 'rxjs/operators';
import { MAX_TIMER_DURATION } from './date';

export const scheduledQueue = <T>(input: ObservableInput<T>): Observable<T> => {
  return scheduled(input, queueScheduler);
};

export const scheduledAsap = <T>(input: ObservableInput<T>): Observable<T> => {
  return scheduled(input, asapScheduler);
};

export const scheduledAsync = <T>(input: ObservableInput<T>): Observable<T> => {
  return scheduled(input, asyncScheduler);
};

export const scheduledAnimationFrame = <T>(input: ObservableInput<T>): Observable<T> => {
  return scheduled(input, animationFrameScheduler);
};

export const observeOnQueue = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    observeOn<T>(queueScheduler),
  );
};

export const observeOnAsap = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    observeOn<T>(asapScheduler),
  );
};

export const observeOnAsync = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    observeOn<T>(asyncScheduler),
  );
};

export const observeOnAnimationFrame = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    observeOn<T>(animationFrameScheduler),
  );
};

export const subscribeOnQueue = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    subscribeOn<T>(queueScheduler),
  );
};

export const subscribeOnAsap = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    subscribeOn<T>(asapScheduler),
  );
};

export const subscribeOnAsync = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    subscribeOn<T>(asyncScheduler),
  );
};

export const subscribeOnAnimationFrame = <T>(): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => source$.pipe(
    subscribeOn<T>(animationFrameScheduler),
  );
};

export const tapSubscribed = <T>(callback: () => void): MonoTypeOperatorFunction<T> => {
  return (source$: Observable<T>) => defer(() => {
    callback();
    return source$;
  });
};

export const fromArray = <T>(items: T[]) => items.length === 0 ? EMPTY : from(items);

export const scheduleAt = (due: number | Date, scheduler?: SchedulerLike): Observable<0> => {
  if (due instanceof Date) {
    const delay = due.getTime() - Date.now();
    return scheduleAt(delay, scheduler);
  }

  if (due <= MAX_TIMER_DURATION) {
    return timer(due, scheduler).pipe(take(1));
  }

  return timer(MAX_TIMER_DURATION, scheduler).pipe(
    take(1),
    switchMap(() => scheduleAt(due - MAX_TIMER_DURATION, scheduler)),
  );
};

export const fromResizeObserver = (element: Element): Observable<ResizeObserverEntry[]> => {
  return new Observable<ResizeObserverEntry[]>(observer => {
    const resizeObserver = new ResizeObserver(entries => {
      observer.next(entries);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  });
};

export const fromIntersectionObserver = (
  element: Element,
  options?: IntersectionObserverInit,
): Observable<IntersectionObserverEntry[]> => {
  return new Observable<IntersectionObserverEntry[]>(observer => {
    const intersectionObserver = new IntersectionObserver((entries) => {
      observer.next(entries);
    }, options);

    intersectionObserver.observe(element);

    return () => {
      intersectionObserver.disconnect();
    };
  });
};

export const fromMutationObserver = (element: Node, options: MutationObserverInit): Observable<MutationRecord[]> => {
  return new Observable<MutationRecord[]>(observer => {
    const mutationObserver = new MutationObserver((mutations) => {
      observer.next(mutations);
    });

    mutationObserver.observe(element, options);

    return () => {
      mutationObserver.disconnect();
    };
  });
};
